import { Link, useNavigate } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TESTIDS } from "@/constants/testIds";
import { COUNTRIES } from "@/data/countries";
import { RULESETS, STATUS } from "@/game/rulesets";
import { hasSavedGame } from "@/hooks/useSavedGame";
import { ArrowRight, Sparkles, BookOpen, PlayCircle, Trophy } from "lucide-react";

const HERO_TEXTURE_URL =
  "https://images.unsplash.com/photo-1629229002497-fe93b56ecd17?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwyfHx0ZXJyYWNvdHRhJTIwZWFydGglMjB0ZXh0dXJlfGVufDB8fHx8MTc4MTgxMjQ2OHww&ixlib=rb-4.1.0&q=85";

export function Home() {
  const navigate = useNavigate();
  const hasSaved = hasSavedGame();

  return (
    <Layout>
      <section
        data-testid={TESTIDS.homePage}
        className="relative overflow-hidden"
      >
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10 opacity-[0.18] bg-cover bg-center"
          style={{ backgroundImage: `url("${HERO_TEXTURE_URL}")` }}
        />
        <div aria-hidden="true" className="absolute inset-0 -z-10 bg-gradient-to-br from-background via-background/60 to-background/95" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <div className="lg:col-span-7">
            <Badge variant="secondary" className="rounded-full text-xs uppercase tracking-widest">
              Zimbabwean strategy · Mancala family
            </Badge>
            <h1 className="mt-5 font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.02] tracking-tight text-balance">
              Tsoro.<br />
              <span className="text-primary">Seeds of strategy.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base sm:text-lg text-foreground/80 leading-relaxed">
              Learn and play Tsoro, the four-row sowing game from Zimbabwe.
              Challenge a friend on the same device, or test yourself against
              a computer opponent. Explore related rulesets from across the
              African continent.
            </p>
            <p className="mt-3 text-sm text-muted-foreground">
              <strong className="text-foreground">No account needed</strong> for vs-AI and local play.
              Create an account to play live against other people, chat, and climb the leaderboard.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                data-testid={TESTIDS.playNowBtn}
                size="lg"
                className="rounded-full px-7 h-12 text-base"
                onClick={() => navigate("/play")}
              >
                <PlayCircle className="h-5 w-5 mr-2" /> Play now
              </Button>
              <Button
                data-testid={TESTIDS.learnRulesBtn}
                size="lg"
                variant="outline"
                className="rounded-full px-7 h-12 text-base"
                onClick={() => navigate("/learn")}
              >
                <BookOpen className="h-5 w-5 mr-2" /> Learn to play
              </Button>
              {hasSaved && (
                <Button
                  data-testid={TESTIDS.continueGameBtn}
                  size="lg"
                  variant="secondary"
                  className="rounded-full px-7 h-12 text-base"
                  onClick={() => navigate("/play/standard?continue=1")}
                >
                  <ArrowRight className="h-5 w-5 mr-2" /> Continue saved game
                </Button>
              )}
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-2"><Sparkles className="h-4 w-4" /> Interactive tutorial</span>
              <span className="inline-flex items-center gap-2"><Trophy className="h-4 w-4" /> 3 AI difficulties</span>
              <span className="inline-flex items-center gap-2">Local 2-player pass &amp; play</span>
            </div>
          </div>

          {/* Right: stylised board preview */}
          <div className="lg:col-span-5">
            <Card className="board-surface p-5 rounded-3xl border-0 shadow-2xl">
              <div className="grid grid-cols-8 gap-2">
                {Array.from({ length: 32 }).map((_, i) => {
                  const row = Math.floor(i / 8);
                  const isOuter = row === 0 || row === 3;
                  return (
                    <div
                      key={i}
                      className="aspect-square pit-base rounded-full flex items-center justify-center"
                    >
                      {isOuter && (
                        <span className="block w-3 h-3 rounded-full seed-token" />
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 flex items-center justify-between text-xs text-foreground/80">
                <span className="uppercase tracking-widest">Standard 4×8 board</span>
                <span className="font-mono">32 counters</span>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Variants preview */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Country variants</p>
            <h2 className="font-display text-3xl sm:text-4xl mt-1">Tsoro and its cousins</h2>
          </div>
          <Link to="/variants" className="text-sm font-medium text-primary hover:underline inline-flex items-center gap-1">
            View all <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {COUNTRIES.slice(0, 8).map((country) => {
            const rs = RULESETS.find((r) => r.country.startsWith(country.name.split(" ")[0])) || null;
            const playable = rs?.status === STATUS.PLAYABLE;
            return (
              <Link
                key={country.slug}
                to={`/rules/${country.slug}`}
                data-testid={TESTIDS.variantCard(country.slug)}
              >
                <Card className="p-4 h-full hover:border-primary/60 transition-colors group">
                  <div className="flex items-center justify-between">
                    <span className="text-2xl">{country.flag}</span>
                    <Badge
                      variant={playable ? "default" : "outline"}
                      className="rounded-full text-[10px] uppercase tracking-widest"
                    >
                      {rs?.status || "Coming soon"}
                    </Badge>
                  </div>
                  <p className="font-display text-lg mt-3">{country.name}</p>
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-3">
                    {country.blurb}
                  </p>
                  <p className="mt-3 text-xs font-medium text-primary inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    Read rules <ArrowRight className="h-3 w-3" />
                  </p>
                </Card>
              </Link>
            );
          })}
        </div>
      </section>
    </Layout>
  );
}
